import DiffMatchPatch from 'diff-match-patch'
import { kCombinedCitationRegex } from '@/util/agent-citations'

export type DiffSegment = {
  op: 'equal' | 'insert' | 'delete'
  text: string
}

/**
 * Splits text into tokens where each citation — "[authority](ref_x)" or "[ref_x]" —
 * is one atomic token, and each other word or whitespace run is its own token. Word
 * atomicity avoids mid-word splits like "th" → "Main"; citation atomicity keeps a
 * citation whole within one diff segment, since a citation fragmented across del/ins
 * boundaries can't be re-rendered as a citation and leaks raw link syntax into the
 * redline.
 */
function tokenize(text: string): string[] {
  const tokens: string[] = []
  let last = 0
  for (const match of text.matchAll(kCombinedCitationRegex)) {
    const index = match.index ?? 0
    tokens.push(...(text.slice(last, index).match(/\S+|\s+/g) || []))
    tokens.push(match[0])
    last = index + match[0].length
  }
  tokens.push(...(text.slice(last).match(/\S+|\s+/g) || []))
  return tokens
}

/**
 * Maps tokens to unique single characters so diff-match-patch can diff
 * at the word level (same technique as its built-in diff_linesToChars_).
 * Note: limited to 65,535 unique tokens (char code ceiling).
 */
function tokensToChars(tokensA: string[], tokensB: string[]): { charsA: string; charsB: string; tokenArray: string[] } {
  const tokenMap = new Map<string, string>()
  const tokenArray: string[] = []

  function encode(tokens: string[]): string {
    return tokens
      .map((token) => {
        let char = tokenMap.get(token)
        if (char === undefined) {
          if (tokenArray.length >= 65535) {
            throw new Error('Token limit exceeded: document too large for word-level diffing')
          }
          char = String.fromCharCode(tokenArray.length)
          tokenMap.set(token, char)
          tokenArray.push(token)
        }
        return char
      })
      .join('')
  }

  return { charsA: encode(tokensA), charsB: encode(tokensB), tokenArray }
}

/**
 * Word-level diff within a single paragraph/block.
 */
function diffWords(oldText: string, newText: string): DiffSegment[] {
  const dmp = new DiffMatchPatch()

  const tokensA = tokenize(oldText)
  const tokensB = tokenize(newText)
  const { charsA, charsB, tokenArray } = tokensToChars(tokensA, tokensB)

  const diffs = dmp.diff_main(charsA, charsB)
  dmp.diff_cleanupSemantic(diffs)

  return diffs.map(([op, chars]) => ({
    op: op === -1 ? 'delete' : op === 1 ? 'insert' : 'equal',
    text: Array.from(chars)
      .map((c) => tokenArray[c.charCodeAt(0)])
      .join(''),
  }))
}

/**
 * Splits text into paragraphs, preserving the separators.
 * Handles clean double newlines (\n\n), triple+ newlines (\n\n\n),
 * and whitespace-only lines (\n    \n) which appear in indented markdown lists.
 */
function splitParagraphs(text: string): string[] {
  return text.split(/((?:\n[ \t]*)+\n)/)
}

/**
 * Normalizes a paragraph for matching: collapses all whitespace runs to a single space
 * and trims. This makes "    *   Item" match "*   Item" (indentation difference)
 * while preserving bullet markers and content for accurate matching.
 */
function normalizeForMatching(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Computes a structured diff by first aligning paragraphs, then doing
 * word-level diffing within each matched paragraph pair. This prevents
 * the diff algorithm from creating nonsensical cross-paragraph alignments.
 */
export function computeDiff(oldText: string, newText: string): DiffSegment[] {
  const dmp = new DiffMatchPatch()

  // First: paragraph-level diff to align blocks.
  // Normalize paragraphs for matching so indentation/marker changes don't prevent alignment.
  const oldParas = splitParagraphs(oldText)
  const newParas = splitParagraphs(newText)
  const { charsA, charsB } = tokensToChars(oldParas.map(normalizeForMatching), newParas.map(normalizeForMatching))
  const paraDiffs = dmp.diff_main(charsA, charsB)
  // Don't run diff_cleanupSemantic here — each char is an entire paragraph,
  // and semantic cleanup can merge adjacent edits across paragraph boundaries.

  const result: DiffSegment[] = []

  // Collect consecutive delete/insert paragraphs for paired word-level diffing
  let pendingDeleted = ''
  let pendingInserted = ''

  const flushPending = () => {
    if (!pendingDeleted && !pendingInserted) return

    if (pendingDeleted && pendingInserted) {
      // Split back into individual paragraphs and diff each pair
      const delParas = splitParagraphs(pendingDeleted).filter((s) => s.length > 0)
      const insParas = splitParagraphs(pendingInserted).filter((s) => s.length > 0)

      // Separate content paragraphs from separators
      const isSeparator = (s: string) => s.trim() === ''
      const delBlocks = delParas.filter((s) => !isSeparator(s))
      const insBlocks = insParas.filter((s) => !isSeparator(s))

      // Diff matched pairs word-by-word
      const maxPairs = Math.min(delBlocks.length, insBlocks.length)
      for (let j = 0; j < maxPairs; j++) {
        if (j > 0) result.push({ op: 'equal', text: '\n\n' })
        result.push(...diffWords(delBlocks[j], insBlocks[j]))
      }
      // Remaining unmatched paragraphs
      for (let j = maxPairs; j < delBlocks.length; j++) {
        result.push({ op: 'equal', text: '\n\n' })
        result.push({ op: 'delete', text: delBlocks[j] })
      }
      for (let j = maxPairs; j < insBlocks.length; j++) {
        result.push({ op: 'equal', text: '\n\n' })
        result.push({ op: 'insert', text: insBlocks[j] })
      }
    } else if (pendingDeleted) {
      result.push({ op: 'delete', text: pendingDeleted })
    } else {
      result.push({ op: 'insert', text: pendingInserted })
    }
    pendingDeleted = ''
    pendingInserted = ''
  }

  // Walk diff results, mapping char positions back to original (non-normalized) paragraphs.
  let oldIdx = 0
  let newIdx = 0
  for (const [op, chars] of paraDiffs) {
    const len = chars.length

    if (op === 0) {
      flushPending()
      // Paragraphs matched by normalized content. Check if the originals actually differ
      // (e.g., indentation change). If so, word-diff them; otherwise emit as equal.
      for (let k = 0; k < len; k++) {
        const oldPara = oldParas[oldIdx + k]
        const newPara = newParas[newIdx + k]
        if (oldPara === newPara || (oldPara.trim() === '' && newPara.trim() === '')) {
          // Identical, or both are whitespace-only separators (e.g., "\n    \n" vs "\n\n")
          result.push({ op: 'equal', text: newPara })
        } else {
          result.push(...diffWords(oldPara, newPara))
        }
      }
      oldIdx += len
      newIdx += len
    } else if (op === -1) {
      pendingDeleted += oldParas.slice(oldIdx, oldIdx + len).join('')
      oldIdx += len
    } else {
      pendingInserted += newParas.slice(newIdx, newIdx + len).join('')
      newIdx += len
    }
  }
  flushPending()

  return result
}

// ── Change grouping utilities ──

export type ChangeGroup = { type: 'equal'; text: string } | { type: 'change'; id: number; deleted: string; inserted: string }

/** Groups raw diff segments into numbered change groups (consecutive del/ins become one change). */
export function groupDiffSegments(segments: DiffSegment[]): ChangeGroup[] {
  const result: ChangeGroup[] = []
  let changeId = 0
  let i = 0

  while (i < segments.length) {
    if (segments[i].op === 'equal') {
      result.push({ type: 'equal', text: segments[i].text })
      i++
      continue
    }

    let deleted = ''
    let inserted = ''
    while (i < segments.length && segments[i].op !== 'equal') {
      if (segments[i].op === 'delete') deleted += segments[i].text
      if (segments[i].op === 'insert') inserted += segments[i].text
      i++
    }

    result.push({ type: 'change', id: changeId++, deleted, inserted })
  }

  return result
}

/**
 * Extracts only the changed sections (plus one paragraph of context before/after)
 * from a list of change groups. Preserves original change IDs for accept/reject.
 */
export function extractChangedSection(groups: ChangeGroup[]): ChangeGroup[] {
  const firstChangeIdx = groups.findIndex((g) => g.type === 'change')
  if (firstChangeIdx === -1) return []

  let lastChangeIdx = firstChangeIdx
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i].type === 'change') {
      lastChangeIdx = i
      break
    }
  }

  // Start at the first change, include 1 equal group after for trailing context
  const start = firstChangeIdx
  const end = Math.min(groups.length, lastChangeIdx + 2)
  const section = groups.slice(start, end)

  // Trim trailing context to its first paragraph only
  const last = section[section.length - 1]
  if (section.length > 1 && last.type === 'equal') {
    const paragraphs = last.text.split(/\n\n+/)
    if (paragraphs.length > 1) {
      section[section.length - 1] = { type: 'equal', text: paragraphs[0] }
    }
  }

  return section
}
